import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StatsGrid from '../dashboard/components/StatsGrid';
import ProductStatsList from './components/ProductStatsList';
import DailyStatsCharts from './components/DailyStatsCharts';
import MarketingStatsList from './components/MarketingStatsList';
import CustomerStatsList from './components/CustomerStatsList';
import ExpenseStatsList from './components/ExpenseStatsList';
import '../business/Business.css';
import '../dashboard/Dashboard.css';
import '../../operator-app/DashboardPage.css';
import './AnalyticsPage.css';

const PERIODS = [
  { id: 1, label: 'Сегодня' },
  { id: 2, label: 'Вчера' },
  { id: 3, label: 'Текущая неделя' },
  { id: 6, label: 'Прошлая неделя' },
  { id: 4, label: 'Текущий месяц' },
  { id: 7, label: 'Прошлый месяц' },
  { id: 8, label: 'Этот год' },
  { id: 9, label: 'Прошлый год' },
  { id: 5, label: 'За все время' },
];

const SECTIONS = [
  { id: 'charts', label: 'График продаж' },
  { id: 'products', label: 'Популярность товаров' },
  { id: 'marketing', label: 'Маркетинг' },
  { id: 'customers', label: 'Клиенты' },
  { id: 'expenses', label: 'Расходы' },
];

function formatUpdatedLabel(date) {
  const time = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return `Обновлено сегодня, ${time}`;
}

function AnalyticsPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const [periodId, setPeriodId] = useState(1);
  const [sectionId, setSectionId] = useState('charts');
  const [updatedLabel, setUpdatedLabel] = useState('Обновление…');
  const periodTabRefs = useRef({});

  useLayoutEffect(() => {
    const el = periodTabRefs.current[periodId];
    el?.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [periodId]);

  const handleBack = () => {
    navigate(`/branch/${branchId ?? 'store'}`);
  };

  const handleStatsLoaded = useCallback(() => {
    setUpdatedLabel(formatUpdatedLabel(new Date()));
  }, []);

  const handlePeriod = (id) => {
    setPeriodId(id);
    setUpdatedLabel('Обновление…');
  };

  return (
    <div className="main-page-root analytics-page">
      <div className="main-page-mobile">
        <header className="analytics-topbar">
          <div className="analytics-topbarIn">
            <div className="analytics-topbarRow">
              <button
                type="button"
                className="analytics-back"
                onClick={handleBack}
                aria-label="Назад"
                title="Назад"
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M15 6l-6 6 6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className="analytics-topbarText">
                <div className="analytics-title">Аналитика</div>
                <div className="analytics-subtitle">{updatedLabel}</div>
              </div>
              <div className="analytics-topbarSpacer" />
              <div className="analytics-live">
                <i />
                Live-данные
              </div>
            </div>
            <div className="analytics-periods" role="tablist" aria-label="Период">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  ref={(node) => {
                    periodTabRefs.current[p.id] = node;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={periodId === p.id}
                  className={
                    periodId === p.id
                      ? 'analytics-period analytics-period--active'
                      : 'analytics-period'
                  }
                  onClick={() => handlePeriod(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        <main className="main-page-content">
          <StatsGrid
            periodId={periodId}
            variant="analytics"
            onLoaded={handleStatsLoaded}
          />

          <div className="analytics-tabs" role="tablist" aria-label="Раздел аналитики">
            {SECTIONS.map((s) => {
              const selected = sectionId === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls={`analytics-section-${s.id}`}
                  className={selected ? 'analytics-tab analytics-tab--active' : 'analytics-tab'}
                  onClick={() => setSectionId(s.id)}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          <div className="analytics-sectionBody">
            <DailyStatsCharts
              branchKey={branchId}
              active={sectionId === 'charts'}
            />
            <ProductStatsList
              branchKey={branchId}
              periodId={periodId}
              active={sectionId === 'products'}
            />
            <MarketingStatsList
              periodId={periodId}
              active={sectionId === 'marketing'}
            />
            <CustomerStatsList
              branchKey={branchId}
              active={sectionId === 'customers'}
            />
            <ExpenseStatsList
              branchKey={branchId}
              periodId={periodId}
              active={sectionId === 'expenses'}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

export default AnalyticsPage;
